// src/pages/admin/finance/FinanceDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAllInvoices, useAllOrders } from '../../../hooks/useAdminData';
import { COLORS } from '../../../styles/tokens';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { invoiceIsTest, isUnpaid, isUncharged, summarizeInvoices } from '../../../utils/financeSummary';

const functions   = getFunctions(undefined, 'europe-west1');
const holdFn      = httpsCallable(functions, 'holdPayout');
const triggerFn   = httpsCallable(functions, 'triggerPayout');
const exportFn    = httpsCallable(functions, 'exportFinanceReport');
const resolveAlertFn = httpsCallable(functions, 'resolveAdminAlert');

// Fakturabeløp lagres i hele kroner
const kr = n => `${Math.round(n ?? 0).toLocaleString('nb-NO')} kr`;

const PAGE_SIZE = 50;

const ALERT_LABELS = {
  dispute:            'Kortselskap-innsigelse',
  payment_failed:     'Betaling feilet',
  transfer_failed:    'Utbetaling til sjåfør feilet',
  transfer_reversed:  'Utbetaling til sjåfør reversert',
  connect_restricted: 'Sjåførs Stripe-konto trenger oppmerksomhet',
  unmatched_event:    'Ukjent Stripe-hendelse (undersøk manuelt)',
};

// adminAlerts lagrer alltid entitetens id i feltet `userId`, uansett entitetstype
// (se functions/src/adminAudit.js) — `entityType` avgjør hvilken admin-side den lenker til.
function alertLink(a) {
  if (a.entityType === 'order') return `/admin/orders/${a.userId}`;
  if (a.entityType === 'user')  return `/admin/users/${a.userId}`;
  return null;
}

export default function FinanceDashboard() {
  const { invoices: allInvoices, loading: invLoading } = useAllInvoices();
  const { orders, loading: ordLoading } = useAllOrders();
  // Testkontoene avgjør test/ekte for ordrer fra før merket fantes. Egen spørring uten orderBy,
  // så brukere uten createdAt ikke faller ut.
  const [testUids, setTestUids] = useState(null); // null = ikke lastet
  // Ekte penger er standard; testdata ligger bak en egen fane så de aldri blandes inn i tallene
  const [env, setEnv] = useState('real'); // 'real' | 'test'
  const [shown, setShown] = useState(PAGE_SIZE);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo,   setExportTo]   = useState('');
  const [exporting,  setExporting]  = useState(false);
  const [exportFile, setExportFile] = useState(null); // { url, filename, rowCount }
  const [exportErr,  setExportErr]  = useState('');
  const isMobile = useIsMobile();
  const [alerts,      setAlerts]      = useState([]);
  const [resolvingId, setResolvingId] = useState(null);
  const [alertsErr,   setAlertsErr]   = useState('');

  const loading = invLoading || ordLoading || testUids === null;
  const ordersById = useMemo(() => new Map(orders.map(o => [o.id, o])), [orders]);
  const { real, test } = useMemo(() => {
    const split = { real: [], test: [] };
    if (testUids === null) return split; // ikke klassifiser før testkontoene er kjent
    allInvoices.forEach(inv => split[invoiceIsTest(inv, ordersById, testUids) ? 'test' : 'real'].push(inv));
    return split;
  }, [allInvoices, ordersById, testUids]);
  const invoices = env === 'real' ? real : test;
  const summary  = useMemo(() => summarizeInvoices(invoices), [invoices]);

  function switchEnv(next) {
    setEnv(next);
    setShown(PAGE_SIZE);
    if (exportFile) URL.revokeObjectURL(exportFile.url);
    setExportFile(null);
  }

  async function loadAlerts() {
    try {
      const snap = await getDocs(query(
        collection(db, 'adminAlerts'),
        where('resolvedAt', '==', null),
        orderBy('createdAt', 'desc'),
        limit(100)
      ));
      setAlerts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAlertsErr('');
    } catch (e) {
      console.error('loadAlerts failed:', e);
      setAlertsErr('Kunne ikke laste betalingsvarsler — prøv å laste siden på nytt');
    }
  }

  async function resolveAlert(alertId) {
    setResolvingId(alertId);
    setAlertsErr('');
    try {
      await resolveAlertFn({ alertId });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) {
      setAlertsErr(e.message ?? 'Kunne ikke markere som løst');
    } finally {
      setResolvingId(null);
    }
  }

  useEffect(() => { loadAlerts(); }, []);
  useEffect(() => {
    getDocs(query(collection(db, 'users'), where('isTestAccount', '==', true)))
      .then(snap => setTestUids(new Set(snap.docs.map(d => d.id))))
      // Uten testkontoene kan ikke test og ekte skilles trygt — vis heller alt som ekte enn å skjule penger
      .catch(e => { console.error('loadTestUsers failed:', e); setTestUids(new Set()); });
  }, []);

  async function runExport() {
    if (!exportFrom || !exportTo) { setExportErr('Velg fra og til dato'); return; }
    setExporting(true); setExportErr('');
    if (exportFile) URL.revokeObjectURL(exportFile.url);
    setExportFile(null);
    try {
      const res = await exportFn({ fromDate: exportFrom, toDate: exportTo, environment: env });
      const { csv, filename, rowCount } = res.data;
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      setExportFile({ url, filename, rowCount });
    } catch (e) {
      setExportErr(e.message ?? 'Eksport feilet');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div style={{ padding:isMobile?16:24, flex:1, overflowY:'auto' }}>
      <h2 style={{ margin:'0 0 16px', fontSize:18, fontWeight:700 }}>Finansoversikt</h2>

      <EnvTabs env={env} onChange={switchEnv} realCount={real.length} testCount={test.length} />
      {env === 'test' && (
        <div role="status" style={{ background:COLORS.warningBg, border:`1px solid ${COLORS.warningBorder}`, color:COLORS.warningText, borderRadius:8, padding:'10px 14px', fontSize:13, fontWeight:600, marginBottom:20 }}>
          🧪 Testdata — dette er ikke ekte penger. Tallene under kommer fra testkontoer og Stripes testmodus.
        </div>
      )}

      {(alerts.length > 0 || alertsErr) && (
        <section style={{ marginBottom:32 }}>
          <h3 style={{ fontSize:14, fontWeight:700, margin:'0 0 12px' }}>Betalingsvarsler</h3>
          {alertsErr && <p style={{ color:COLORS.dangerText, fontSize:13, margin:'0 0 8px' }}>{alertsErr}</p>}
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {alerts.map(a => (
              <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:COLORS.warningBg, border:`1px solid ${COLORS.warningBorder}`, borderRadius:8, padding:'10px 14px', fontSize:13 }}>
                <div>
                  <strong>{ALERT_LABELS[a.type] ?? a.type}</strong>
                  {a.error && (
                    <>
                      {' · '}
                      <span>{a.error}</span>
                    </>
                  )}
                  {alertLink(a) && (
                    <>
                      {' · '}
                      <a href={alertLink(a)} style={{ color:COLORS.blue }}>Åpne →</a>
                    </>
                  )}
                </div>
                <button
                  onClick={() => resolveAlert(a.id)}
                  disabled={resolvingId === a.id}
                  style={{ padding:'5px 12px', border:'none', borderRadius:6, background:COLORS.textDark, color:'#fff', fontSize:12, cursor:resolvingId === a.id ? 'not-allowed' : 'pointer', opacity:resolvingId === a.id ? 0.6 : 1 }}
                >
                  Merk som løst
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Hva Tippi har tjent — hovedtallet */}
      <div style={{ ...styles.hero, borderColor: env === 'test' ? COLORS.warningBorder : COLORS.successBorder }}>
        <div style={styles.cardLabel}>Tippi-gebyr opptjent{env === 'test' ? ' (test)' : ''}</div>
        <div data-testid="fees-earned" style={{ fontSize:isMobile?30:36, fontWeight:700, color: env === 'test' ? COLORS.warningText : COLORS.successText }}>
          {loading ? '…' : kr(summary.feesEarned)}
        </div>
        {!loading && (
          <div style={styles.heroSub}>
            <span><strong>{kr(summary.feesThisMonth)}</strong> denne måneden</span>
            <span><strong>{kr(summary.feesLast30Days)}</strong> siste 30 dager</span>
            <span>{summary.earnedCount} betalte oppdrag</span>
            {summary.feesPending > 0 && <span style={{ color:COLORS.textLight }}>+ {kr(summary.feesPending)} venter på trekk</span>}
          </div>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(2,1fr)', gap:16, marginBottom:summary.refundLossAbsorbed > 0 ? 16 : 32 }}>
        <StatCard label="Omsetning (trukket fra bestillere)" value={loading ? '…' : kr(summary.turnover)} />
        <StatCard label="Ikke utbetalt til sjåfører" value={loading ? '…' : kr(summary.unpaidToDrivers)} />
      </div>
      {!loading && summary.unchargedCount > 0 && (
        <p style={{ fontSize:13, color:COLORS.textMid, margin:'0 0 16px' }}>
          ℹ️ {summary.unchargedCount} faktura{summary.unchargedCount === 1 ? '' : 'er'} på {kr(summary.unchargedAmount)} ble opprettet uten at kortet ble belastet. Ingen penger har flyttet seg, så de er ikke med i tallene over.
        </p>
      )}
      {!loading && summary.refundLossAbsorbed > 0 && (
        <p style={{ fontSize:13, color:COLORS.dangerText, margin:'0 0 32px' }}>
          ⚠️ Tippi har båret {kr(summary.refundLossAbsorbed)} i refusjoner der sjåføren beholdt utbetalingen. Det er ikke trukket fra gebyret over.
        </p>
      )}

      {/* Payout list */}
      <section style={{ marginBottom:32 }}>
        <h3 style={{ fontSize:14, fontWeight:700, margin:'0 0 12px' }}>Utbetalinger</h3>
        <PayoutTable payouts={invoices.slice(0, shown)} />
        {loading && <p style={{ color:COLORS.textLight, fontSize:13 }}>Laster…</p>}
        {!loading && invoices.length === 0 && (
          <p style={{ color:COLORS.textLight, fontSize:13 }}>
            {env === 'real' ? 'Ingen ekte betalinger ennå.' : 'Ingen testbetalinger.'}
          </p>
        )}
        {shown < invoices.length && (
          <button onClick={() => setShown(n => n + PAGE_SIZE)} style={{ marginTop:8, padding:'7px 14px', border:`1px solid ${COLORS.border}`, borderRadius:6, background:'transparent', color:COLORS.textDark, cursor:'pointer', fontSize:13 }}>
            Last inn flere ({invoices.length - shown} igjen)
          </button>
        )}
      </section>

      {/* CSV Export */}
      <section>
        <h3 style={{ fontSize:14, fontWeight:700, margin:'0 0 12px' }}>
          Eksporter rapport{' '}
          <span style={{ fontSize:12, fontWeight:600, color: env === 'test' ? COLORS.warningText : COLORS.textLight }}>
            ({env === 'real' ? 'kun ekte betalinger' : 'kun testdata'})
          </span>
        </h3>
        <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
          <input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} style={inputStyle} />
          <span style={{ color:COLORS.textLight }}>–</span>
          <input type="date" value={exportTo}   onChange={e => setExportTo(e.target.value)}   style={inputStyle} />
          <button onClick={runExport} disabled={exporting} style={{ padding:'8px 16px', background:COLORS.primary, color: COLORS.onPrimary, border:'none', borderRadius:6, cursor:exporting?'not-allowed':'pointer', opacity:exporting?0.6:1, fontSize:13 }}>
            {exporting ? 'Genererer…' : 'Last ned CSV'}
          </button>
        </div>
        {exportErr && <p style={{ color:COLORS.dangerText, fontSize:13, marginTop:8 }}>{exportErr}</p>}
        {exportFile && (
          <p style={{ fontSize:13, marginTop:8 }}>
            <a href={exportFile.url} download={exportFile.filename} style={{ color:COLORS.blue }}>Last ned {exportFile.filename}</a>
            <span style={{ color:COLORS.textLight }}> ({exportFile.rowCount} rader)</span>
          </p>
        )}
      </section>
    </div>
  );
}

function EnvTabs({ env, onChange, realCount, testCount }) {
  const tabs = [
    { value: 'real', label: 'Ekte betalinger', count: realCount },
    { value: 'test', label: '🧪 Test',           count: testCount },
  ];
  return (
    <div role="tablist" style={{ display:'flex', gap:6, marginBottom:16 }}>
      {tabs.map(t => {
        const active = env === t.value;
        const accent = t.value === 'test' ? COLORS.warning : COLORS.primary;
        return (
          <button
            key={t.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.value)}
            style={{
              padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:active ? 700 : 600, cursor:'pointer',
              border:`2px solid ${active ? accent : COLORS.border}`,
              background: active ? accent : 'transparent',
              color: active ? '#fff' : COLORS.textMid,
            }}
          >
            {t.label} <span style={{ opacity:0.8, fontWeight:600 }}>({t.count})</span>
          </button>
        );
      })}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ background:COLORS.white, border:`1px solid ${COLORS.border}`, borderRadius:10, padding:'16px 20px' }}>
      <div style={styles.cardLabel}>{label}</div>
      <div style={{ fontSize:22, fontWeight:700, color:COLORS.textDark }}>{value}</div>
    </div>
  );
}

function payoutLabel(p) {
  if (isUncharged(p))          return 'ingen betaling trukket';
  if (p.status === 'refunded') return 'refundert';
  if (p.stripeTransferId)      return 'utbetalt';
  if (p.payoutsHeld)           return 'holdt';
  if (p.status === 'pending')  return 'venter på trekk';
  if (p.status === 'captured') return 'klar til utbetaling';
  return p.status ?? '—';
}

function PayoutTable({ payouts }) {
  const [busy,    setBusy]    = useState({});
  const [reason,  setReason]  = useState('');
  const [message, setMessage] = useState(null); // { ok, text }

  async function hold(driverUid) {
    await run(driverUid, async () => {
      const { data } = await holdFn({ driverUid, reason });
      return `Utbetalinger holdt (${data.invoicesHeld} faktura(er) venter)`;
    });
  }

  async function pay(driverUid) {
    if (!window.confirm('Overføre alt sjåføren har til gode nå? Holdet oppheves.')) return;
    await run(driverUid, async () => {
      const { data } = await triggerFn({ driverUid, reason });
      const failed = data.failed.length ? ` — ${data.failed.length} feilet, se varsel på brukeren` : '';
      return `Utbetalt ${data.paidCount} faktura(er)${failed}`;
    });
  }

  async function run(driverUid, action) {
    if (!reason.trim()) { setMessage({ ok: false, text: 'Skriv begrunnelse i feltet under tabellen' }); return; }
    setBusy(prev => ({ ...prev, [driverUid]: true }));
    setMessage(null);
    try {
      // Fakturaene lyttes i sanntid, så tabellen oppdaterer seg selv
      setMessage({ ok: true, text: await action() });
    } catch (e) {
      setMessage({ ok: false, text: e.message ?? 'Noe gikk galt' });
    } finally {
      setBusy(prev => ({ ...prev, [driverUid]: false }));
    }
  }

  return (
    <div>
      <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', minWidth:520, borderCollapse:'collapse', fontSize:13, marginBottom:8 }}>
        <thead>
          <tr style={{ borderBottom:`2px solid ${COLORS.border}` }}>
            {['Sjåfør','Beløp','Status','Dato','Handling'].map(h =>
              <th key={h} style={{ padding:'7px 10px', color:COLORS.textLight, fontWeight:600, textTransform:'uppercase', fontSize:11, textAlign:'left' }}>{h}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {payouts.map(p => (
            <tr key={p.id} style={{ borderBottom:`1px solid ${COLORS.border}` }}>
              <td style={{ padding:'8px 10px' }}>{p.driverUid ?? '—'}</td>
              <td style={{ padding:'8px 10px' }}>{p.driverPayout ? kr(p.driverPayout) : '—'}</td>
              <td style={{ padding:'8px 10px' }}>{payoutLabel(p)}</td>
              <td style={{ padding:'8px 10px' }}>{p.createdAt?.toDate?.().toLocaleDateString('nb-NO') ?? '—'}</td>
              <td style={{ padding:'8px 10px', display:'flex', gap:6 }}>
                {isUnpaid(p) && !isUncharged(p) && !p.payoutsHeld && (
                  <button disabled={busy[p.driverUid]} onClick={() => hold(p.driverUid)} style={smallBtn(COLORS.warning, busy[p.driverUid])}>Hold</button>
                )}
                {isUnpaid(p) && !isUncharged(p) && (
                  <button disabled={busy[p.driverUid]} onClick={() => pay(p.driverUid)} style={smallBtn(COLORS.success, busy[p.driverUid])}>Betal nå</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {message && <p style={{ color: message.ok ? COLORS.successText : COLORS.dangerText, fontSize:13, margin:'0 0 8px' }}>{message.text}</p>}
      <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Begrunnelse for hold/utbetaling (påkrevd)…" style={{ ...inputStyle, width:'100%', maxWidth:400 }} />
    </div>
  );
}

const styles = {
  hero:      { background:COLORS.white, border:'2px solid', borderRadius:12, padding:'20px 24px', marginBottom:16 },
  heroSub:   { display:'flex', flexWrap:'wrap', gap:'4px 20px', marginTop:8, fontSize:13, color:COLORS.textMid },
  cardLabel: { fontSize:11, color:COLORS.textLight, fontWeight:600, textTransform:'uppercase', marginBottom:6 },
};

const inputStyle = { padding:'7px 10px', border:`1px solid ${COLORS.border}`, borderRadius:6, fontSize:13, background:COLORS.white, color:COLORS.textDark };
const smallBtn = (color, disabled) => ({ padding:'4px 10px', background:color, color:'#fff', border:'none', borderRadius:4, cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.6:1, fontSize:12 });
